'use client';

import { useCallback, useEffect, useMemo, useState, useRef, type CSSProperties } from 'react';
import { normalizePhoneNumberPaste, formatPhoneForDisplay, isValidBrazilianPhoneNumber, toE164 } from '../lib/phoneUtils';
import { PhoneNumberInput } from './PhoneNumberInput';
import { CallHistoryList } from './CallHistoryList';
import { classifyHistoryQuery, historyPhoneDigits, type CallHistoryItem } from '../lib/callHistory';
import { resolveDensity, useInputProfile, type DialerDensity } from '../lib/deviceProfile';
import type { LoginConfirmation } from '../lib/dialerTabs';
import { API_BASE_URL, getAuthHeaders } from '../lib/widgetApi';
import { campaignFormCacheKey, campaignFormRequestFromCallInfo, fetchCampaignForm } from '../lib/campaignFormClient';
import { findFormValue, type CampaignFormResponse } from '../lib/campaignFormTypes';
import { CLICK_TO_CALL_EVENT, type ClickToCallTaskPublic } from '../lib/clickToCallTypes';
import { nationalDigitsFromE164 } from '../lib/clickToCallClient';
import {
  useCallOperator,
  useCallOperatorState,
  useCallOperatorCurrentCall,
  useCallOperatorCurrentCallControls,
  useCallOperatorCurrentCallInfo,
  useCallOperatorSuccessQualifications,
  useCallOperatorDiscardQualifications,
  useCallOperatorControls,
  useCallOperatorAudioInputMuted,
} from '../lib/callixSdk';
import { useSignal } from '../hooks/useSignal';
import { useOperatorBreak } from '../hooks/useOperatorBreak';
import { useHaptic } from '../hooks/useHaptic';
import { useDTMF } from '../hooks/useDTMF';
import { useDebugLogger, addLog } from '../hooks/useDebugLogger';
import { DebugPanel } from './DebugPanel';
import { DialerNoticeBanner } from './DialerNotice';
import { LiveCaptionPanel } from './LiveCaptionPanel';
import { useDialerNotices } from '../hooks/useDialerNotices';
import { DraggableClientInfoModal } from './DraggableClientInfoModal';
import { useDialerConfig } from '../widget/index';
// CSS refatorado com estrutura header/main/footer flex
import '../styles/mobileDialer-refactor.css';
import '../styles/mobileDialer-history.css';
// Dimensionamento proporcional ao container (por último: sobrescreve os tamanhos fixos)
import '../styles/mobileDialer-fluid.css';

// ============================================
// INTERCEPTADOR GLOBAL DE FETCH PARA DEBUG
// Captura TODAS as requisições HTTP do app
// ============================================
if (typeof window !== 'undefined' && !(window as unknown as Record<string, boolean>).__fetchIntercepted__) {
  (window as unknown as Record<string, boolean>).__fetchIntercepted__ = true;
  
  const originalFetch = window.fetch.bind(window);
  
  window.fetch = async function(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const requestId = `req_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url;
    const method = init?.method || (input instanceof Request ? input.method : 'GET');
    
    // Capturar headers da requisição
    const requestHeaders: Record<string, string> = {};
    if (init?.headers) {
      if (init.headers instanceof Headers) {
        init.headers.forEach((value, key) => {
          requestHeaders[key] = key.toLowerCase().includes('token') || key.toLowerCase().includes('auth')
            ? `${value.substring(0, 30)}...(${value.length} chars)`
            : value;
        });
      } else if (Array.isArray(init.headers)) {
        init.headers.forEach(([key, value]) => {
          requestHeaders[key] = key.toLowerCase().includes('token') || key.toLowerCase().includes('auth')
            ? `${value.substring(0, 30)}...(${value.length} chars)`
            : value;
        });
      } else {
        Object.entries(init.headers).forEach(([key, value]) => {
          requestHeaders[key] = key.toLowerCase().includes('token') || key.toLowerCase().includes('auth')
            ? `${String(value).substring(0, 30)}...(${String(value).length} chars)`
            : String(value);
        });
      }
    }
    
    const startTime = performance.now();
    
    console.log(`%c[FETCH ${requestId}] ➡️ ${method} ${url}`, 'color: #4CAF50; font-weight: bold');
    console.log(`[FETCH ${requestId}] Headers:`, JSON.stringify(requestHeaders, null, 2));
    if (init?.body) {
      try {
        const bodyStr = typeof init.body === 'string' ? init.body : JSON.stringify(init.body);
        console.log(`[FETCH ${requestId}] Body:`, bodyStr.substring(0, 500));
      } catch {
        console.log(`[FETCH ${requestId}] Body: [não serializável]`);
      }
    }
    console.log(`[FETCH ${requestId}] credentials: ${init?.credentials || 'default'}, mode: ${init?.mode || 'default'}`);
    
    // Adicionar ao debug logger
    addLog('network', 'Request', `➡️ ${method} ${url.substring(0, 60)}...`, {
      requestId,
      method,
      url,
      headers: requestHeaders,
      credentials: init?.credentials,
    });
    
    try {
      const response = await originalFetch(input, init);
      const endTime = performance.now();
      const duration = Math.round(endTime - startTime);
      
      // Capturar headers da resposta
      const responseHeaders: Record<string, string> = {};
      response.headers.forEach((value, key) => {
        responseHeaders[key] = value;
      });
      
      const statusColor = response.ok ? '#4CAF50' : '#F44336';
      console.log(`%c[FETCH ${requestId}] ⬅️ ${response.status} ${response.statusText} (${duration}ms)`, `color: ${statusColor}; font-weight: bold`);
      console.log(`[FETCH ${requestId}] Response Headers:`, JSON.stringify(responseHeaders, null, 2));
      
      // Adicionar ao debug logger
      addLog(response.ok ? 'network' : 'error', 'Response', `⬅️ ${response.status} ${url.substring(0, 40)}... (${duration}ms)`, {
        requestId,
        status: response.status,
        statusText: response.statusText,
        duration,
        headers: responseHeaders,
      });
      
      // Se for erro 401 ou 403, logar detalhes extras
      if (response.status === 401 || response.status === 403) {
        console.error(`%c[FETCH ${requestId}] ❌ AUTH ERROR ${response.status}`, 'color: #F44336; font-weight: bold; font-size: 14px');
        console.error(`[FETCH ${requestId}] URL completa:`, url);
        console.error(`[FETCH ${requestId}] Todos os headers enviados:`, requestHeaders);
        
        // Tentar ler o body do erro
        try {
          const clonedResponse = response.clone();
          const errorBody = await clonedResponse.text();
          console.error(`[FETCH ${requestId}] Response Body:`, errorBody);
          addLog('error', 'AuthError', `❌ ${response.status} - ${errorBody.substring(0, 200)}`, {
            requestId,
            url,
            headers: requestHeaders,
            responseBody: errorBody,
          });
        } catch (e) {
          console.error(`[FETCH ${requestId}] Não foi possível ler body do erro:`, e);
        }
      }
      
      return response;
    } catch (error) {
      const endTime = performance.now();
      const duration = Math.round(endTime - startTime);
      
      console.error(`%c[FETCH ${requestId}] ❌ NETWORK ERROR (${duration}ms)`, 'color: #F44336; font-weight: bold');
      console.error(`[FETCH ${requestId}] Error:`, error);
      
      addLog('error', 'NetworkError', `❌ ${url.substring(0, 40)}... - ${String(error)}`, {
        requestId,
        url,
        error: String(error),
        duration,
      });
      
      throw error;
    }
  };
  
  console.log('%c[FETCH INTERCEPTOR] ✅ Ativo - todas as requisições serão logadas', 'color: #2196F3; font-weight: bold; font-size: 12px');
}

// ============================================
// DADOS DO CONTATO (formulário da campanha)
// Uma requisição ao backend (/api/campaign-form) com cache por contato, compartilhada
// com o modal de informações; os valores vêm indexados pelo nome do campo em
// minúsculas (ver src/lib/campaignFormTypes.ts).
// ============================================

type FinalContactData = Record<string, string>;

interface ContactDataState {
  finalData: FinalContactData;
  isLoading: boolean;
  error: string | null;
}

// API_BASE_URL e getAuthHeaders ficam em ../lib/widgetApi (compartilhados com o histórico).

/**
 * Busca valor em finalData por lista de candidatos, na ordem, sem distinguir caixa/acentos
 */
function findValue(finalData: FinalContactData, candidates: string[]): string | undefined {
  return findFormValue({ values: finalData }, candidates);
}

// ============================================
// TIPOS DO COMPONENTE
// ============================================

type MobileDialerProps = {
  isAuthenticated: boolean;
  username: string | null;
  onLogin: (username: string) => Promise<void> | void;
  onLogout: () => void;
  isLoadingSession: boolean;
  sessionError: string | null;
  /** Mensagem de progresso do login (ex.: "Autenticando no Callix...") */
  sessionStatus?: string | null;
  /** Usuário conhecido (HTML ou último login) aguardando confirmação para entrar */
  loginConfirmation?: LoginConfirmation | null;
  /** Trocar de usuário na tela de confirmação */
  onCancelLoginConfirmation?: () => void;
  /** Outra aba segura a conexão: trazer o discador para esta aba */
  onTakeOverSession?: () => void;
  /** Sair do discador em todas as abas */
  onLogoutAllTabs?: () => void;
  /** Densidade da interface: 'auto' (mouse = compacta, toque = confortável), 'compact' ou 'comfortable' */
  density?: DialerDensity;
  showClientInfoModal?: boolean;  // Se true, exibe DraggableClientInfoModal em chamadas de campanha
};

type QualificationResult = 'success' | 'failure';

/**
 * MobileDialer - Wrapper que renderiza tela de login quando não autenticado,
 * ou delega para AuthenticatedMobileDialer quando autenticado (com provider).
 */
export function MobileDialer(props: MobileDialerProps) {
  // Garante FontAwesome carregado para os ícones do modelo
  useEffect(() => {
    const existing = document.querySelector('link[data-mobile-dialer-fa="true"]');
    if (!existing) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css';
      link.dataset.mobileDialerFa = 'true';
      document.head.appendChild(link);
    }
  }, []);

  // Densidade: compacta com mouse/hover (desktop, inclusive 1024px por VGA), confortável em toque.
  // Decidida pelo tipo de ponteiro, nunca pela largura da janela.
  const inputProfile = useInputProfile();
  const density = resolveDensity(props.density, inputProfile);

  // Se não autenticado, renderiza tela de login (SEM usar hooks do SDK)
  if (!props.isAuthenticated) {
    return <MobileDialerLogin {...props} density={density} />;
  }

  // Se autenticado, renderiza o dialer completo (COM hooks do SDK)
  return <AuthenticatedMobileDialer {...props} density={density} />;
}

/**
 * MobileDialerLogin - Tela de login (não usa hooks do SDK)
 *
 * Estados:
 * - carregando: spinner + etapa atual do login (nunca parece "travado")
 * - confirmação: usuário conhecido (HTML/localStorage) com botão "Entrar"
 * - formulário: campo de usuário + botão
 */
function MobileDialerLogin({
  username,
  onLogin,
  isLoadingSession,
  sessionError,
  sessionStatus,
  loginConfirmation,
  onCancelLoginConfirmation,
  onTakeOverSession,
  onLogoutAllTabs,
  density,
}: MobileDialerProps) {
  const [loginInput, setLoginInput] = useState(username ?? '');
  const [loginError, setLoginError] = useState<string | null>(null);

  useEffect(() => {
    setLoginInput(username ?? '');
  }, [username]);

  const submitLogin = useCallback(
    async (value: string) => {
      const trimmed = value.trim();
      if (!trimmed) {
        setLoginError('Informe o usuário Callix.');
        return;
      }
      setLoginError(null);
      try {
        await Promise.resolve(onLogin(trimmed));
      } catch (error) {
        setLoginError('Falha ao autenticar.');
        console.error('Erro login', error);
      }
    },
    [onLogin]
  );

  const handleLogin = useCallback(() => submitLogin(loginInput), [submitLogin, loginInput]);

  const showConfirmation = Boolean(loginConfirmation) && !isLoadingSession;
  const loadingUser = (loginConfirmation?.username || loginInput || username || '').trim();

  return (
    <div className="phone-container" data-density={density === 'compact' ? 'compact' : 'comfortable'}>
      <div className="screen dialer-screen login-screen active" id="dialerScreen">
        <div className="dialer-header">
          <div className="status-container">
            <button type="button" className={`status-badge ${isLoadingSession ? 'connecting' : 'available'}`}>
              <span className="status-dot" />
              <span className="status-text">{isLoadingSession ? 'Conectando' : 'Login'}</span>
            </button>
          </div>
        </div>

        {isLoadingSession ? (
          <div className="login-loading" role="status" aria-live="polite">
            <div className="login-spinner" aria-hidden="true" />
            {loadingUser && <div className="login-loading-user">{loadingUser}</div>}
            <div className="login-status">{sessionStatus || 'Conectando...'}</div>
          </div>
        ) : showConfirmation && loginConfirmation ? (
          loginConfirmation.elsewhere ? (
            <div className="login-confirm" data-login-state="elsewhere">
              <div className="login-confirm-label">Discador ativo em outra aba</div>
              <div className="login-user-chip">
                <i className="fas fa-user-check" aria-hidden="true" />
                <span>{loginConfirmation.username}</span>
              </div>
              {sessionError && <div className="input-error">{sessionError}</div>}
              <button type="button" className="login-confirm-btn login-confirm-btn-blue" id="takeOverBtn" onClick={() => onTakeOverSession?.()}>
                <i className="fas fa-arrow-down" aria-hidden="true" />
                <span>Usar o discador nesta aba</span>
              </button>
              <button type="button" className="login-secondary-btn login-secondary-danger" id="logoutAllBtn" onClick={() => onLogoutAllTabs?.()}>
                Sair em todas as abas
              </button>
              <p className="login-hint">O Callix aceita uma conexão por usuário: você já está conectado em outra aba deste navegador e pode continuar por lá.</p>
            </div>
          ) : (
            <div className="login-confirm" data-login-state="choose">
              <div className="login-confirm-label">Entrar como</div>
              <div className="login-user-chip">
                <i className="fas fa-user" aria-hidden="true" />
                <span>{loginConfirmation.username}</span>
              </div>
              {sessionError && <div className="input-error">{sessionError}</div>}
              {!sessionError && loginConfirmation.note && <div className="login-note">{loginConfirmation.note}</div>}
              <button type="button" className="login-confirm-btn" id="loginConfirmBtn" onClick={() => void submitLogin(loginConfirmation.username)}>
                <i className="fas fa-sign-in-alt" aria-hidden="true" />
                <span>Entrar</span>
              </button>
              {!loginConfirmation.locked && onCancelLoginConfirmation && (
                <button type="button" className="login-secondary-btn" onClick={onCancelLoginConfirmation}>
                  Usar outro usuário
                </button>
              )}
              <p className="login-hint">Ao entrar, todas as abas abertas passam a usar o discador. Vai usar o Callix web? Então não entre aqui: nada é conectado sem a sua confirmação.</p>
            </div>
          )
        ) : (
          <>
            <div className="phone-number-display">
              <input
                type="text"
                className="phone-input"
                placeholder="Digite seu usuário Callix"
                value={loginInput}
                onChange={(e) => setLoginInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
            </div>
            {loginError && <div className="input-error">{loginError}</div>}
            {sessionError && <div className="input-error">{sessionError}</div>}
            <div className="call-actions">
              <div className="empty-space" />
              <button type="button" className="call-button" onClick={handleLogin} aria-label="Entrar">
                <i className="fas fa-sign-in-alt" />
              </button>
              <div className="empty-space" />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Registro do histórico (formato devolvido por /api/call-history)
type CallHistoryRecord = CallHistoryItem;

/**
 * Normaliza o primeiro nome: primeira letra maiúscula, resto minúscula
 * Ex: "WERIQUI FERREIRA DE SOUZA" -> "Weriqui"
 * Ex: "weriqui ferreira" -> "Weriqui"
 */
function normalizeFirstName(fullName: string): string {
  if (!fullName) return '';
  const firstName = fullName.trim().split(/\s+/)[0] || '';
  if (!firstName) return '';
  return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
}

/**
 * Busca valor em um dicionário por lista de candidatos (case-insensitive)
 */
function findFieldValue(fields: Record<string, string> | undefined, candidates: string[]): string | undefined {
  if (!fields) return undefined;
  const entries = Object.entries(fields);
  const lowerCandidates = candidates.map(c => c.toLowerCase());
  for (const [key, value] of entries) {
    if (lowerCandidates.includes(key.toLowerCase())) {
      return value;
    }
  }
  return undefined;
}

/**
 * Gera mensagem de template para WhatsApp com dados do contato
 * Se não houver dados do contato, retorna mensagem genérica
 */
function generateWhatsAppMessage(
  operatorName: string,
  contactFields: Record<string, string> | FinalContactData | undefined
): string {
  const operatorFirstName = normalizeFirstName(operatorName);
  
  // Se não tem campos, retornar mensagem genérica curta
  if (!contactFields || typeof contactFields !== 'object' || Object.keys(contactFields).length === 0) {
    console.log('[WhatsApp Template] Sem dados de contato, usando mensagem genérica');
    return `Olá! Aqui é *${operatorFirstName}*, do Grupo Villela. Estou entrando em contato para dar continuidade ao nosso atendimento. Posso ajudar?`;
  }
  
  // Debug: logar campos recebidos
  console.log('[WhatsApp Template] Operador:', operatorName, '-> Primeiro nome:', operatorFirstName);
  console.log('[WhatsApp Template] Campos recebidos:', contactFields);
  console.log('[WhatsApp Template] Chaves disponíveis:', Object.keys(contactFields));
  
  // Buscar valores dos campos
  const prospectName = findFieldValue(contactFields as Record<string, string>, 
    ['DECISOR', 'SOCIO', 'NOME', 'PROSPECT', 'CLIENTE', 'CONTATO']
  );
  console.log('[WhatsApp Template] Prospect encontrado:', prospectName);
  
  const companyName = findFieldValue(contactFields as Record<string, string>, 
    ['EMPRESA', 'RAZAO SOCIAL', 'RAZÃO SOCIAL', 'RAZAO_SOCIAL', 'COMPANY', 'NOME_EMPRESA']
  );
  console.log('[WhatsApp Template] Empresa encontrada:', companyName);
  
  const potentialValue = findFieldValue(contactFields as Record<string, string>, 
    ['BENEFICIO', 'BENEFÍCIO', 'CAPITAL_RECUPERAR', 'CAPITAL A RECUPERAR', 'POTENCIAL', 'VALOR_RECUPERAR', 'VALOR RECUPERADO']
  );
  console.log('[WhatsApp Template] Potencial encontrado:', potentialValue);

  // Se nenhum campo útil foi encontrado, usar mensagem genérica
  if (!prospectName && !companyName && !potentialValue) {
    console.log('[WhatsApp Template] Nenhum campo útil encontrado, usando mensagem genérica');
    return `Olá! Aqui é *${operatorFirstName}*, do Grupo Villela. Estou entrando em contato para dar continuidade ao nosso atendimento. Posso ajudar?`;
  }

  // Template da mensagem com formatação WhatsApp
  let message = `Oi, *${prospectName || 'tudo bem'}*. Aqui é *${operatorFirstName}*, do Grupo Villela. Empresa líder no mercado de redução de dívidas e recuperação de capital, com mais de 20 anos de experiência.`;

  if (companyName) {
    message += `\n\nVimos que a *${companyName}* tem um débito federal que contém cobranças indevidas.`;
  }

  if (potentialValue) {
    message += `\n\nEm uma análise preliminar, verificamos que é possível recuperar para sua empresa um valor aproximado de *${potentialValue}*.`;
  } else if (companyName) {
    message += `\n\nEm uma análise preliminar, verificamos que é possível recuperar valores significativos para sua empresa.`;
  }

  message += `\n\nGostaríamos de fazer um *diagnóstico gratuito* para te apresentar os valores reais que a empresa pode economizar. Posso te encaminhar o link para autorização?`;

  return message;
}

/**
 * AuthenticatedMobileDialer - Componente que usa hooks do SDK.
 * SÓ é renderizado quando CallixClientProvider está presente (authenticated).
 */
function AuthenticatedMobileDialer({
  username,
  onLogout,
  showClientInfoModal = false,
  density,
}: MobileDialerProps) {
  // Configuração do dialer (skipQualification para chamadas manuais)
  const { skipManualQualification } = useDialerConfig();
  
  const [dialValue, setDialValue] = useState('');
  const [activeScreen, setActiveScreen] = useState<'dialer' | 'call' | 'incallKeypad' | 'history'>('dialer');
  const [isPauseModalOpen, setIsPauseModalOpen] = useState(false);
  const [isQualificationOpen, setIsQualificationOpen] = useState(false);
  const [qualificationResult, setQualificationResult] = useState<QualificationResult>('success');
  const [selectedQualificationId, setSelectedQualificationId] = useState<number | null>(null);
  const [qualificationError, setQualificationError] = useState<string | null>(null);
  const [callDurationSeconds, setCallDurationSeconds] = useState(0);
  const [callError, setCallError] = useState<string | null>(null);
  
  // Estado para modal de informações do cliente (DraggableClientInfoModal)
  const [isClientInfoModalOpen, setIsClientInfoModalOpen] = useState(false);
  
  // Estado para painel de debug (ativado por código secreto)
  const [isDebugPanelOpen, setIsDebugPanelOpen] = useState(false);
  
  // Inicializar o logger de debug
  useDebugLogger();
  
  // Estado de pausa: nome e tempo
  const [currentBreakName, setCurrentBreakName] = useState<string | null>(null);
  const [pauseStartTime, setPauseStartTime] = useState<number | null>(null);
  const [pauseDurationSeconds, setPauseDurationSeconds] = useState(0);
  
  // Estado para dados do contato (buscados via API)
  const [contactData, setContactData] = useState<ContactDataState>({
    finalData: {},
    isLoading: false,
    error: null,
  });

  // Estado para histórico de chamadas
  const [callHistory, setCallHistory] = useState<CallHistoryRecord[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  // Busca no histórico: por número vai ao servidor; por texto filtra as últimas chamadas
  const [historyQuery, setHistoryQuery] = useState('');
  const [historySearchResults, setHistorySearchResults] = useState<CallHistoryRecord[] | null>(null);
  const [isSearchingHistory, setIsSearchingHistory] = useState(false);
  const historyTextPoolRef = useRef<CallHistoryRecord[] | null>(null);
  const historySearchSeqRef = useRef(0);

  // Estado para controle de viva-voz (speaker)
  const [isSpeakerOn, setIsSpeakerOn] = useState(false);
  const [audioOutputDevices, setAudioOutputDevices] = useState<MediaDeviceInfo[]>([]);
  const [speakerDeviceId, setSpeakerDeviceId] = useState<string | null>(null);
  const [defaultDeviceId, setDefaultDeviceId] = useState<string>('default');
  const [speakerSupported, setSpeakerSupported] = useState<boolean | null>(null); // null = não verificado ainda

  // Hooks do SDK - APENAS usados aqui dentro do componente autenticado
  const operator = useCallOperator();
  const operatorState = useCallOperatorState();
  const currentCall = useCallOperatorCurrentCall();
  const { hangup, answer, reject } = useCallOperatorCurrentCallControls();
  const callInfo = useCallOperatorCurrentCallInfo();
  // número da chamada em curso segundo o SDK/motor: vale no espelho (outra aba) e depois de um F5,
  // onde ninguém digitou nada neste teclado
  const activeCallNumber = currentCall?.data?.destinationNumber || currentCall?.callerNumber || '';
  const rawSuccessQualifications = useCallOperatorSuccessQualifications();
  const rawDiscardQualifications = useCallOperatorDiscardQualifications();
  const successQualifications = useMemo(() => rawSuccessQualifications ?? [], [rawSuccessQualifications]);
  const discardQualifications = useMemo(() => rawDiscardQualifications ?? [], [rawDiscardQualifications]);
  const { makeManualCall, finishAfterCall } = useCallOperatorControls();
  const { goOnBreak, goOffBreak } = useOperatorBreak();
  const breaks = useSignal(operator?.breaks) || [];
  const userData = useSignal(operator?.user); // Dados do usuário autenticado (nome, id, login)
  const isOnBreak = operatorState?.state === 'onBreak';
  
  // Hook de mute do microfone (igual ao FloatingDialer)
  const [inputMuted, setInputMuted] = useCallOperatorAudioInputMuted();

  // Hooks de feedback sensorial (padrão Apple)
  const haptic = useHaptic();
  const dtmf = useDTMF({ enabled: true });

  // Ref para armazenar sequência de teclas pressionadas (para detectar código secreto)
  const keySequenceRef = useRef<string>('');
  const keySequenceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Função para verificar código secreto baseado na sequência de teclas
  const checkSecretSequence = useCallback((newSequence: string) => {
    // Códigos secretos: *#0000# ou *#3284# (DEBUG em teclado numérico)
    const SECRET_CODES = ['*#0000#', '*#3284#'];
    
    if (SECRET_CODES.includes(newSequence)) {
      addLog('info', 'Debug', '🔓 Código secreto detectado - abrindo painel de debug');
      setIsDebugPanelOpen(true);
      setDialValue(''); // Limpar o campo
      keySequenceRef.current = '';
      return true;
    }
    return false;
  }, []);

  // Detectar dispositivos de saída de áudio para viva-voz
  useEffect(() => {
    const detectAudioOutputDevices = async () => {
      try {
        // Verificar se setSinkId é suportado (necessário para trocar dispositivo de saída)
        const audioElement = document.createElement('audio');
        const sinkIdSupported = 'setSinkId' in audioElement;
        
        if (!sinkIdSupported) {
          console.log('[MobileDialer] setSinkId não suportado neste navegador');
          setSpeakerSupported(false);
          return;
        }

        // Em mobile, precisamos solicitar permissão de áudio para enumerar dispositivos
        // O getUserMedia com audio:true é necessário para obter labels dos dispositivos
        let hasAudioPermission = false;
        
        // Primeiro, tentar enumerar para ver se já temos permissão
        let devices = await navigator.mediaDevices.enumerateDevices();
        let audioOutputs = devices.filter(d => d.kind === 'audiooutput');
        
        // Se os labels estão vazios, precisamos pedir permissão
        const hasLabels = audioOutputs.some(d => d.label && d.label.length > 0);
        
        if (!hasLabels && audioOutputs.length > 0) {
          console.log('[MobileDialer] Labels vazios, solicitando permissão de áudio...');
          try {
            // Solicitar permissão de áudio (não apenas microfone)
            const stream = await navigator.mediaDevices.getUserMedia({ 
              audio: true,
              // Em alguns navegadores, especificar mais detalhes pode ajudar
            });
            hasAudioPermission = true;
            
            // Parar o stream imediatamente - só precisamos da permissão
            stream.getTracks().forEach(track => track.stop());
            
            // Re-enumerar dispositivos agora que temos permissão
            devices = await navigator.mediaDevices.enumerateDevices();
            audioOutputs = devices.filter(d => d.kind === 'audiooutput');
            console.log('[MobileDialer] Permissão concedida. Dispositivos:', audioOutputs.length, audioOutputs);
          } catch (permError) {
            console.log('[MobileDialer] Usuário negou permissão de áudio:', permError);
            // Continuar mesmo sem labels - usar deviceIds
          }
        } else {
          hasAudioPermission = true;
        }
        
        console.log('[MobileDialer] Dispositivos de saída encontrados:', audioOutputs.length, audioOutputs);
        
        if (audioOutputs.length <= 1) {
          // Apenas um dispositivo (ou nenhum) - viva-voz não disponível
          console.log('[MobileDialer] Apenas um dispositivo de saída - viva-voz não disponível');
          setSpeakerSupported(false);
          return;
        }

        setAudioOutputDevices(audioOutputs);
        
        // Tentar identificar o speaker (geralmente contém "speaker" no label)
        // Em mobile, pode ser "Speakerphone" ou similar
        const speakerDevice = audioOutputs.find(d => 
          d.label.toLowerCase().includes('speaker') ||
          d.label.toLowerCase().includes('alto-falante') ||
          d.label.toLowerCase().includes('viva-voz')
        );
        
        // Identificar dispositivo padrão (earpiece/fone)
        const defaultDevice = audioOutputs.find(d => 
          d.deviceId === 'default' || 
          d.label.toLowerCase().includes('default') ||
          d.label.toLowerCase().includes('earpiece') ||
          d.label.toLowerCase().includes('fone')
        ) || audioOutputs[0];

        if (speakerDevice && speakerDevice.deviceId !== defaultDevice?.deviceId) {
          setSpeakerDeviceId(speakerDevice.deviceId);
          setDefaultDeviceId(defaultDevice?.deviceId || 'default');
          setSpeakerSupported(true);
          console.log('[MobileDialer] Viva-voz disponível:', speakerDevice.label);
        } else if (audioOutputs.length > 1) {
          // Se não encontrou um "speaker" específico mas tem múltiplos dispositivos,
          // usar o segundo dispositivo como alternativa
          const altDevice = audioOutputs.find(d => d.deviceId !== defaultDevice?.deviceId);
          if (altDevice) {
            setSpeakerDeviceId(altDevice.deviceId);
            setDefaultDeviceId(defaultDevice?.deviceId || 'default');
            setSpeakerSupported(true);
            console.log('[MobileDialer] Usando dispositivo alternativo para viva-voz:', altDevice.label);
          } else {
            setSpeakerSupported(false);
          }
        } else {
          setSpeakerSupported(false);
        }
      } catch (error) {
        console.error('[MobileDialer] Erro ao detectar dispositivos de áudio:', error);
        setSpeakerSupported(false);
      }
    };

    detectAudioOutputDevices();

    // Re-detectar quando dispositivos mudam (ex: bluetooth conectado)
    const handleDeviceChange = () => {
      console.log('[MobileDialer] Mudança de dispositivos detectada');
      detectAudioOutputDevices();
    };

    navigator.mediaDevices?.addEventListener?.('devicechange', handleDeviceChange);
    return () => {
      navigator.mediaDevices?.removeEventListener?.('devicechange', handleDeviceChange);
    };
  }, []);

  // ID da pausa inicial (Reunião = 5)
  const INITIAL_BREAK_ID = 5;
  const INITIAL_BREAK_NAME = 'Reunião';

  // Derivações de estado de chamada (movido para cima para usar no useEffect)
  const callState = operatorState?.state;

  // Entrar em pausa "Reunião" quando operador ficar idle (ao invés de ficar disponível)
  const hasEnteredInitialBreakRef = useRef(false);
  useEffect(() => {
    // Aguardar:
    // 1. Breaks carregarem
    // 2. Estado ser 'idle' (operador conectado e pronto)
    // 3. Não ter entrado em pausa ainda
    // 4. Não estar já em pausa
    if (breaks.length === 0 || callState !== 'idle' || hasEnteredInitialBreakRef.current || isOnBreak) {
      return;
    }

    if (!goOnBreak) {
      return;
    }

    hasEnteredInitialBreakRef.current = true;
    
    // Verificar se a pausa "Reunião" existe
    const reuniaoBreak = breaks.find((b: { id: number; name: string }) => 
      b.id === INITIAL_BREAK_ID || b.name.toLowerCase() === INITIAL_BREAK_NAME.toLowerCase()
    );
    
    if (reuniaoBreak) {
      console.log('[MobileDialer] Operador conectado (idle), entrando em pausa inicial:', reuniaoBreak.name);
      Promise.resolve(goOnBreak(reuniaoBreak.id)).then(() => {
        console.log('[MobileDialer] Pausa inicial aplicada com sucesso:', reuniaoBreak.name);
        setCurrentBreakName(reuniaoBreak.name);
        setPauseStartTime(Date.now());
      }).catch((err) => {
        console.error('[MobileDialer] Falha ao entrar em pausa:', err);
        hasEnteredInitialBreakRef.current = false; // Permitir retry
      });
    } else {
      // Se não encontrar a pausa "Reunião", usar a primeira pausa disponível
      const firstBreak = breaks[0] as { id: number; name: string };
      console.log('[MobileDialer] Pausa "Reunião" não encontrada, usando:', firstBreak.name);
      Promise.resolve(goOnBreak(firstBreak.id)).then(() => {
        console.log('[MobileDialer] Pausa inicial aplicada com sucesso:', firstBreak.name);
        setCurrentBreakName(firstBreak.name);
        setPauseStartTime(Date.now());
      }).catch((err) => {
        console.error('[MobileDialer] Falha ao entrar em pausa:', err);
        hasEnteredInitialBreakRef.current = false; // Permitir retry
      });
    }
  }, [breaks, callState, isOnBreak, goOnBreak]);

  // Debug: log breaks when they change
  useEffect(() => {
    console.log('[MobileDialer] Breaks loaded:', breaks);
    addLog('state', 'Breaks', `Pausas carregadas: ${breaks.length}`, breaks);
  }, [breaks]);

  // Debug: log state changes
  useEffect(() => {
    console.log('[MobileDialer] Call state changed:', callState);
    addLog('state', 'CallState', `Estado da chamada: ${callState}`, { callState, isOnBreak });
    
    // Nota: NÃO limpamos a sessão automaticamente no estado offline
    // porque o SDK pode entrar brevemente nesse estado durante a inicialização
    // e isso causaria perda do token antes de buscar dados do contato
    if (callState === 'offline') {
      console.warn('[MobileDialer] Estado OFFLINE detectado - verifique a conexão');
      addLog('warn', 'Connection', '⚠️ Estado OFFLINE detectado');
    }
  }, [callState, isOnBreak]);

  // Debug: log callInfo quando mudar
  useEffect(() => {
    if (callInfo) {
      console.log('[MobileDialer] Call info atualizado:', {
        type: callInfo?.type,
        info: callInfo?.type === 'campaign' ? {
          campaign: callInfo.info.campaign,
          campaignContact: callInfo.info.campaignContact,
        } : callInfo,
      });
    }
  }, [callInfo]);

  // Dados do contato: uma requisição ao backend (/api/campaign-form), com cache por
  // contato (a mesma do modal de informações). Começa assim que a chamada toca.
  const campaignFormRequest = useMemo(
    () =>
      callInfo?.type === 'campaign'
        ? campaignFormRequestFromCallInfo(callInfo.info, currentCall?.data?.campaignContactPhoneId ?? null)
        : null,
    [callInfo, currentCall]
  );
  const campaignFormKey = campaignFormRequest ? campaignFormCacheKey(campaignFormRequest) : null;
  const campaignFormRequestRef = useRef(campaignFormRequest);
  campaignFormRequestRef.current = campaignFormRequest;

  useEffect(() => {
    const request = campaignFormRequestRef.current;
    if (!request || !campaignFormKey) {
      setContactData({ finalData: {}, isLoading: false, error: null });
      return;
    }

    let cancelled = false;
    let refreshTimer: ReturnType<typeof setTimeout> | null = null;
    setContactData({ finalData: {}, isLoading: true, error: null });
    addLog('debug', 'FetchContact', 'Buscando formulário do contato', { contactId: request.id, source: request.source });

    const apply = (form: CampaignFormResponse) => {
      setContactData({ finalData: form.values, isLoading: false, error: null });
      addLog('info', 'FetchContact', `✅ Formulário: ${form.fields.length} campos (${form.valuesSource}, layout ${form.layoutSource})`, {
        contactId: request.id,
        partial: form.partial === true,
      });
    };

    fetchCampaignForm(request)
      .then((form) => {
        if (cancelled) return;
        apply(form);
        if (form.partial) {
          // Rótulos técnicos (sem layout): refaz uma vez em seguida, quando o layout costuma já estar cacheado
          refreshTimer = setTimeout(() => {
            if (cancelled) return;
            fetchCampaignForm(request, { force: true })
              .then((refreshed) => {
                if (!cancelled) apply(refreshed);
              })
              .catch(() => undefined);
          }, 3000);
        }
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error('[MobileDialer] Erro ao buscar o formulário:', error);
        setContactData({ finalData: {}, isLoading: false, error: error instanceof Error ? error.message : 'Erro ao buscar dados' });
      });

    return () => {
      cancelled = true;
      if (refreshTimer) clearTimeout(refreshTimer);
    };
  }, [campaignFormKey]);

  const callStateStr = callState as string | undefined;
  // avisos do diagnóstico: microfone/saída de som, partida do operador, sessão derrubada, chamada que falhou, offline
  const { notice: dialerNotice, dismiss: dismissDialerNotice, recheckDevices } = useDialerNotices();
  // altura da faixa de aviso: o teclado encolhe na mesma medida (--notice-h no CSS fluido) para o botão de ligar não cobrir as teclas
  const [noticeHeight, setNoticeHeight] = useState(0);
  const isError = callStateStr === 'startupError' || callStateStr === 'offline' || dialerNotice?.source === 'device' || dialerNotice?.source === 'session';
  const isIdle =
    !callStateStr ||
    callStateStr === 'starting' ||
    callStateStr === 'idle' ||
    callStateStr === 'manualCallSetup' ||
    callStateStr === 'onBreak';
  const isConnecting = callState === 'manualCallSetup' || callState === 'manualCallRinging' || callState === 'callRinging';
  const isInCall = callState === 'callInProgress';
  const isAfterCall = callState === 'afterCall';

  // Obter campaignCallInfo para o modal de informações do cliente
  const campaignCallInfo = useMemo(() => {
    if (!callInfo || callInfo.type !== 'campaign') {
      return null;
    }
    return callInfo.info;
  }, [callInfo]);

  // Abrir modal de informações do cliente para chamadas de campanha (quando showClientInfoModal=true)
  useEffect(() => {
    if (!showClientInfoModal) return; // Não faz nada se o modal não estiver habilitado
    
    if ((isInCall || isConnecting) && callInfo?.type === 'campaign') {
      setIsClientInfoModalOpen(true);
    }
  }, [showClientInfoModal, isInCall, isConnecting, callInfo]);

  // Fechar modal de cliente quando chamada terminar (mas não durante afterCall)
  useEffect(() => {
    if (!showClientInfoModal) return;
    
    if (!isInCall && !isConnecting && !isAfterCall) {
      setIsClientInfoModalOpen(false);
    }
  }, [showClientInfoModal, isInCall, isConnecting, isAfterCall]);

  // Timer de duração da chamada
  useEffect(() => {
    if (!isInCall) {
      setCallDurationSeconds(0);
      return;
    }
    const interval = setInterval(() => {
      setCallDurationSeconds((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [isInCall]);

  // Timer de duração da pausa
  useEffect(() => {
    if (!isOnBreak || !pauseStartTime) {
      return;
    }
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - pauseStartTime) / 1000);
      setPauseDurationSeconds(elapsed);
    }, 1000);
    return () => clearInterval(interval);
  }, [isOnBreak, pauseStartTime]);

  // Limpar estado de pausa quando sair do break externamente
  useEffect(() => {
    if (!isOnBreak && currentBreakName) {
      setCurrentBreakName(null);
      setPauseStartTime(null);
      setPauseDurationSeconds(0);
    }
  }, [isOnBreak, currentBreakName]);

  // Click-to-call vindo da API: mostra o número no visor como se tivesse sido digitado;
  // o ClickToCallAgent dispara makeManualCall e a tela de chamada assume pelo estado do SDK.
  useEffect(() => {
    const onClickToCall = (event: Event) => {
      const task = (event as CustomEvent<ClickToCallTaskPublic>).detail;
      if (!task?.phone) return;
      setCallError(null);
      setDialValue(nationalDigitsFromE164(task.phone).slice(0, 32));
      setActiveScreen('dialer');
      addLog('event', 'Call', `🖱️ Click-to-call recebido: ${task.phoneDisplay}`, { taskId: task.id, source: task.source });
    };
    window.addEventListener(CLICK_TO_CALL_EVENT, onClickToCall);
    return () => window.removeEventListener(CLICK_TO_CALL_EVENT, onClickToCall);
  }, []);

  // Controle de telas baseado no estado da chamada
  useEffect(() => {
    console.log('[MobileDialer] Screen control effect - callState:', callState, 'isInCall:', isInCall, 'isConnecting:', isConnecting, 'isAfterCall:', isAfterCall);
    
    if (isInCall || isConnecting) {
      setActiveScreen('call');
    } else if (isAfterCall || isIdle || isOnBreak) {
      // Voltar para dialer quando: afterCall, idle, ou onBreak
      // Isso garante que a tela volta ao discador quando a chamada termina
      setActiveScreen('dialer');
    }
  }, [isInCall, isConnecting, isAfterCall, isIdle, isOnBreak, callState]);

  // Após ligação -> qualificação (com lógica para pular chamadas manuais)
  // SINCRONIZADO com FloatingDialer e InlineDialer
  useEffect(() => {
    if (isAfterCall) {
      // DEBUG: Logar estado completo para diagnóstico
      console.log('[MobileDialer] 📋 afterCall triggered - callInfo:', JSON.stringify(callInfo, null, 2));
      console.log('[MobileDialer] 📋 afterCall - callInfo?.type:', callInfo?.type);
      console.log('[MobileDialer] 📋 afterCall - skipManualQualification:', skipManualQualification);
      console.log('[MobileDialer] 📋 afterCall - successQualifications:', successQualifications.length);
      console.log('[MobileDialer] 📋 afterCall - discardQualifications:', discardQualifications.length);
      
      // 1. Verificar se deve pular qualificação (campanhas com skipCallQualification)
      const shouldSkipCampaignQualification =
        callInfo?.type === 'campaign' &&
        callInfo.info.campaign.skipCallQualification === true;

      if (shouldSkipCampaignQualification && finishAfterCall) {
        console.log('[MobileDialer] 🚀 Campanha com skipCallQualification - finalizando automaticamente');
        void Promise.resolve(
          finishAfterCall({
            result: 'success',
            qualificationId: undefined,
          })
        );
        return;
      }

      // 2. Chamadas manuais SEMPRE pulam qualificação (não faz sentido qualificar chamada manual)
      const isManualCall = callInfo?.type === 'manual';
      
      if (isManualCall && finishAfterCall) {
        console.log('[MobileDialer] 🚀 Chamada MANUAL detectada - finalizando automaticamente (chamadas manuais NUNCA exibem qualificação)');
        void Promise.resolve(
          finishAfterCall({
            result: 'success',
            qualificationId: undefined,
          })
        );
        return;
      }

      // 3. Se não há qualificações configuradas, também pular
      const hasNoQualifications = successQualifications.length === 0 && discardQualifications.length === 0;
      if (hasNoQualifications && finishAfterCall) {
        console.log('[MobileDialer] 🚀 Sem qualificações configuradas - finalizando automaticamente');
        void Promise.resolve(
          finishAfterCall({
            result: 'success',
            qualificationId: undefined,
          })
        );
        return;
      }

      // 4. Só abre modal de qualificação se for campanha COM qualificações
      console.log('[MobileDialer] ⚠️ Abrindo modal de qualificação - tipo:', callInfo?.type);
      const hasSuccess = successQualifications.length > 0;
      setQualificationResult(hasSuccess ? 'success' : 'failure');
      setSelectedQualificationId(null);
      setQualificationError(null);
      setIsQualificationOpen(true);
    } else {
      setIsQualificationOpen(false);
    }
  }, [isAfterCall, callInfo, finishAfterCall, successQualifications, discardQualifications, skipManualQualification]);

  // Número formatado para UI
  const formattedDial = useMemo(() => formatPhoneForDisplay(dialValue), [dialValue]);

  // Prévia do número que será discado quando ele difere do digitado
  // (DDD padrão em número sem DDD, 9 em celular antigo, 0/operadora removidos)
  const dialPreview = useMemo(() => {
    if (/[*#]/.test(dialValue)) {
      return null;
    }
    const digits = dialValue.replace(/\D/g, '');
    if (!digits) {
      return null;
    }
    const normalized = normalizePhoneNumberPaste(digits);
    if (!normalized || normalized === digits || !isValidBrazilianPhoneNumber(normalized)) {
      return null;
    }
    return formatPhoneForDisplay(normalized);
  }, [dialValue]);

  // Helpers
  const sanitizeInput = useCallback((input: string) => normalizePhoneNumberPaste(input), []);

  const handleDigit = useCallback(
    (digit: string) => {
      // Feedback sensorial Apple: háptico + auditivo
      haptic.impactLight();
      dtmf.play(digit);
      
      // Detectar código secreto: acumula teclas pressionadas
      // Reset após 3 segundos de inatividade
      if (keySequenceTimeoutRef.current) {
        clearTimeout(keySequenceTimeoutRef.current);
      }
      keySequenceRef.current += digit;
      
      console.log('[MobileDialer] Sequência atual:', keySequenceRef.current);
      
      // Verificar se é um código secreto
      if (checkSecretSequence(keySequenceRef.current)) {
        console.log('[MobileDialer] 🔓 Código secreto detectado!');
        // Limpar o dialValue pois pode ter dígitos do código secreto
        setDialValue('');
        return; // Não adiciona ao dialValue se for código secreto
      }
      
      // Limitar tamanho da sequência para evitar memory leak
      if (keySequenceRef.current.length > 20) {
        keySequenceRef.current = keySequenceRef.current.slice(-10);
      }
      
      // Reset da sequência após 3 segundos de inatividade
      keySequenceTimeoutRef.current = setTimeout(() => {
        keySequenceRef.current = '';
      }, 3000);
      
      if (isInCall || isConnecting) {
        if (currentCall && typeof currentCall.sendDtmf === 'function') {
          try {
            currentCall.sendDtmf(digit);
          } catch (error) {
            console.error('Falha ao enviar DTMF', error);
          }
        }
        return;
      }
      setDialValue((prev) => (prev + digit).slice(0, 32));
    },
    [currentCall, isInCall, isConnecting, haptic, dtmf, checkSecretSequence]
  );

  const handleBackspace = useCallback(() => {
    haptic.impactLight();
    setDialValue((prev) => prev.slice(0, -1));
  }, [haptic]);

  const handleCall = useCallback(async () => {
    console.log('[MobileDialer] handleCall triggered, dialValue:', dialValue);
    addLog('event', 'Call', '📞 Iniciando chamada manual', { dialValue });
    const sanitized = sanitizeInput(dialValue);
    console.log('[MobileDialer] Sanitized number:', sanitized);
    
    if (!sanitized || !isValidBrazilianPhoneNumber(sanitized)) {
      console.log('[MobileDialer] Invalid phone format');
      addLog('warn', 'Call', '⚠️ Número inválido', { sanitized, isValid: false });
      setCallError('Número inválido. Informe o DDD e o número (fixo com 8 ou celular com 9 dígitos).');
      haptic.notificationError();
      return;
    }
    setCallError(null);
    
    if (!makeManualCall) {
      console.error('[MobileDialer] makeManualCall is not available');
      addLog('error', 'Call', '❌ makeManualCall não disponível');
      setCallError('Função de chamada não disponível.');
      return;
    }
    
    // Formatar para E.164: +55 + número
    const e164Number = toE164(sanitized) ?? `+55${sanitized}`;
    console.log('[MobileDialer] Calling makeManualCall with E.164:', e164Number);
    addLog('info', 'Call', `📞 Ligando para ${e164Number}`, { e164Number });
    haptic.impactMedium();
    try {
      // SDK espera string direta, não objeto
      void makeManualCall(e164Number);
      console.log('[MobileDialer] makeManualCall called successfully');
      addLog('info', 'Call', '✅ makeManualCall executado com sucesso');
    } catch (error) {
      console.error('[MobileDialer] Erro ao iniciar chamada manual:', error);
      addLog('error', 'Call', '❌ Erro ao iniciar chamada', error);
      setCallError('Erro ao iniciar chamada.');
      haptic.notificationError();
    }
  }, [dialValue, makeManualCall, sanitizeInput, haptic]);

  const handleHangup = useCallback(async () => {
    if (!hangup) return;
    addLog('event', 'Call', '📴 Encerrando chamada');
    haptic.impactHeavy();
    
    try {
      await hangup();
      addLog('info', 'Call', '✅ Chamada encerrada');
    } catch (error) {
      console.error('[MobileDialer] Erro ao desligar', error);
    }
  }, [hangup, haptic]);

  const toggleMute = useCallback(() => {
    haptic.impactLight();
    // Usar hook de mute do SDK (igual ao FloatingDialer)
    setInputMuted(!inputMuted);
  }, [inputMuted, setInputMuted, haptic]);

  const toggleSpeaker = useCallback(() => {
    haptic.impactLight();
    
    // Se viva-voz não é suportado, mostrar aviso
    if (speakerSupported === false) {
      console.warn('[MobileDialer] Viva-voz não suportado neste navegador/dispositivo');
      haptic.notificationWarning();
      return;
    }

    // Se ainda não verificamos ou não temos dispositivos
    if (!speakerDeviceId || speakerSupported === null) {
      console.warn('[MobileDialer] Dispositivos de saída não detectados ainda');
      haptic.notificationWarning();
      return;
    }

    const newSpeakerState = !isSpeakerOn;
    const targetDeviceId = newSpeakerState ? speakerDeviceId : defaultDeviceId;

    console.log('[MobileDialer] Alternando viva-voz:', newSpeakerState ? 'LIGADO' : 'DESLIGADO', 'Device:', targetDeviceId);

    try {
      // Usar o SDK para trocar o dispositivo de saída de áudio
      if (operator?.audioOutput) {
        operator.audioOutput.setAudioOutputDevice(targetDeviceId);
        setIsSpeakerOn(newSpeakerState);
        haptic.notificationSuccess();
        console.log('[MobileDialer] Dispositivo de saída alterado com sucesso');
      } else {
        console.warn('[MobileDialer] audioOutput não disponível no operator');
        haptic.notificationError();
      }
    } catch (error) {
      console.error('[MobileDialer] Erro ao alternar viva-voz:', error);
      haptic.notificationError();
    }
  }, [haptic, speakerSupported, speakerDeviceId, defaultDeviceId, isSpeakerOn, operator]);

  // Busca uma página do histórico (rota exige o token de sessão; agent_id é só conferência)
  const requestHistory = useCallback(
    async ({ phone, limit }: { phone?: string; limit: number }): Promise<CallHistoryRecord[]> => {
      const agentId = userData?.id;
      if (!agentId) {
        throw new Error('Aguardando ID do usuário');
      }
      const params = new URLSearchParams({ agent_id: String(agentId), limit: String(limit) });
      if (phone) {
        params.set('phone', phone);
      }
      const response = await fetch(`${API_BASE_URL}/api/call-history?${params.toString()}`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'omit',
      });
      if (!response.ok) {
        throw new Error(
          response.status === 401 || response.status === 403
            ? 'Sessão expirada. Entre novamente para ver o histórico.'
            : `Erro ${response.status}`
        );
      }
      const data = await response.json();
      return (data.items || []) as CallHistoryRecord[];
    },
    [userData?.id]
  );

  // Buscar histórico de chamadas (lista inicial)
  const fetchCallHistory = useCallback(async () => {
    if (!userData?.id) {
      console.log('[MobileDialer] Aguardando ID do usuário para buscar histórico');
      return;
    }

    setIsLoadingHistory(true);
    setHistoryError(null);
    historyTextPoolRef.current = null; // atualizar invalida o cache da busca por texto

    try {
      const items = await requestHistory({ limit: 50 });
      console.log('[MobileDialer] Histórico recebido:', items.length, 'registros');
      setCallHistory(items);
    } catch (error) {
      console.error('[MobileDialer] Erro ao buscar histórico:', error);
      setHistoryError(error instanceof Error && error.message.startsWith('Sessão') ? error.message : 'Erro ao carregar histórico');
      setCallHistory([]);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [requestHistory, userData?.id]);

  // Busca: número (3+ dígitos) consulta todo o histórico no servidor;
  // texto (campanha, cliente, qualificação...) filtra as últimas 200 chamadas.
  const handleHistoryQueryChange = useCallback(
    async (query: string) => {
      setHistoryQuery(query);
      const parsed = classifyHistoryQuery(query);
      const seq = ++historySearchSeqRef.current;

      if (parsed.kind === 'empty') {
        setHistorySearchResults(null);
        setIsSearchingHistory(false);
        return;
      }

      setIsSearchingHistory(true);
      try {
        let results: CallHistoryRecord[];
        if (parsed.kind === 'phone') {
          results = await requestHistory({ phone: parsed.digits, limit: 100 });
        } else {
          if (!historyTextPoolRef.current) {
            historyTextPoolRef.current = await requestHistory({ limit: 200 });
          }
          results = historyTextPoolRef.current;
        }
        if (seq === historySearchSeqRef.current) {
          setHistorySearchResults(results);
        }
      } catch (error) {
        console.error('[MobileDialer] Erro na busca do histórico:', error);
        if (seq === historySearchSeqRef.current) {
          setHistorySearchResults(null); // cai no filtro local da lista carregada
        }
      } finally {
        if (seq === historySearchSeqRef.current) {
          setIsSearchingHistory(false);
        }
      }
    },
    [requestHistory]
  );

  const historySearchHint = useMemo(() => {
    const parsed = classifyHistoryQuery(historyQuery);
    if (parsed.kind === 'phone') {
      return 'Busca por número em todo o histórico';
    }
    if (parsed.kind === 'text') {
      return historySearchResults ? 'Busca por texto nas últimas 200 chamadas' : 'Busca por texto nas chamadas carregadas';
    }
    return null;
  }, [historyQuery, historySearchResults]);

  // Buscar histórico quando mudar para a tela de histórico
  useEffect(() => {
    if (activeScreen === 'history') {
      void fetchCallHistory();
    }
  }, [activeScreen, fetchCallHistory]);

  // Abrir WhatsApp com o número da chamada atual e mensagem template
  const handleWhatsApp = useCallback(() => {
    // Pegar o número do contato da campanha ou o número discado
    let phoneNumber = '';
    
    if (callInfo?.type === 'campaign' && callInfo.info.campaignContact?.phoneNumber) {
      phoneNumber = callInfo.info.campaignContact.phoneNumber;
    } else if (dialValue) {
      phoneNumber = dialValue;
    } else if (activeCallNumber) {
      phoneNumber = activeCallNumber;
    }
    
    // Limpar o número (remover caracteres não numéricos exceto +)
    const cleanNumber = phoneNumber.replace(/[^\d+]/g, '');
    
    // Garantir que tem o código do país (55 para Brasil)
    let whatsappNumber = cleanNumber;
    if (!whatsappNumber.startsWith('+')) {
      if (!whatsappNumber.startsWith('55')) {
        whatsappNumber = '55' + whatsappNumber;
      }
      whatsappNumber = '+' + whatsappNumber;
    }
    
    // Gerar mensagem do template com dados do contato
    // Usar dados da API (contactData.finalData) que já foram buscados durante a chamada
    const operatorName = userData?.name || username || '';
    const message = generateWhatsAppMessage(operatorName, contactData.finalData);
    
    // Codificar mensagem para URL
    const encodedMessage = encodeURIComponent(message);
    
    // Abrir WhatsApp Web/App com o número e mensagem
    const whatsappUrl = `https://wa.me/${whatsappNumber.replace('+', '')}?text=${encodedMessage}`;
    console.log('[MobileDialer] Abrindo WhatsApp com template:', whatsappUrl);
    window.open(whatsappUrl, '_blank');
  }, [callInfo, dialValue, userData, username, contactData.finalData, activeCallNumber]);

  const handleGoOnBreak = useCallback(
    async (breakId: number) => {
      if (!goOnBreak) return;
      try {
        // Encontrar o nome da pausa selecionada
        const selectedBreak = breaks.find((b: { id: number; name: string }) => b.id === breakId);
        await goOnBreak(breakId);
        haptic.notificationSuccess();
        // Salvar nome da pausa e iniciar timer
        setCurrentBreakName(selectedBreak?.name ?? 'Em pausa');
        setPauseStartTime(Date.now());
        setPauseDurationSeconds(0);
        setIsPauseModalOpen(false);
      } catch (error) {
        console.error('Erro ao entrar em pausa', error);
        haptic.notificationError();
      }
    },
    [goOnBreak, breaks, haptic]
  );

  const handleGoOffBreak = useCallback(async () => {
    if (!goOffBreak) return;
    try {
      await goOffBreak();
      haptic.notificationSuccess();
      // Limpar estado de pausa
      setCurrentBreakName(null);
      setPauseStartTime(null);
      setPauseDurationSeconds(0);
      setIsPauseModalOpen(false);
    } catch (error) {
      console.error('Erro ao sair de pausa', error);
      haptic.notificationError();
    }
  }, [goOffBreak, haptic]);

  const handleQualificationConfirm = useCallback(async () => {
    if (!finishAfterCall) return;
    const list = qualificationResult === 'success' ? successQualifications : discardQualifications;
    if (!list.length) {
      setQualificationError('Nenhuma qualificação disponível.');
      haptic.notificationWarning();
      return;
    }
    if (!selectedQualificationId) {
      setQualificationError('Selecione uma qualificação.');
      haptic.notificationWarning();
      return;
    }
    setQualificationError(null);
    haptic.impactMedium();
    try {
      await Promise.resolve(
        finishAfterCall({
          result: qualificationResult,
          qualificationId: selectedQualificationId,
        })
      );
      haptic.notificationSuccess();
      setIsQualificationOpen(false);
    } catch (error) {
      setQualificationError('Erro ao enviar qualificação.');
      haptic.notificationError();
      console.error('Erro finishAfterCall', error);
    }
  }, [discardQualifications, finishAfterCall, qualificationResult, selectedQualificationId, successQualifications, haptic]);

  const formattedCallNumber = useMemo(() => {
    // Para chamadas de campanha, pegar o phoneNumber do campaignContact
    if (callInfo?.type === 'campaign' && callInfo.info.campaignContact?.phoneNumber) {
      return formatPhoneForDisplay(callInfo.info.campaignContact.phoneNumber);
    }
    if (formattedDial) return formattedDial;
    // espelho ou F5 no meio da chamada: o número vem da própria chamada
    return activeCallNumber ? formatPhoneForDisplay(activeCallNumber) : '(--) ----- ----';
  }, [callInfo, formattedDial, activeCallNumber]);

  const callStatusLabel = useMemo(() => {
    if (isConnecting) return 'Ligando...';
    if (isInCall) return 'Em chamada';
    if (isAfterCall) return 'Encerrando...';
    if (callStateStr === 'manualCallFailed') return 'Chamada não concluída';
    if (isError) return dialerNotice?.title ?? 'Erro de conexão';
    if (isOnBreak) return 'Em pausa';
    return 'Disponível';
  }, [callStateStr, dialerNotice, isAfterCall, isConnecting, isError, isInCall, isOnBreak]);

  // Status badge class - reflete corretamente erro/pausa/disponível
  const statusBadgeClass = useMemo(() => {
    if (isError) return 'error';
    if (isOnBreak) return 'paused';
    return 'available';
  }, [isError, isOnBreak]);

  // Status text para o badge do dialer (nome da pausa quando em pausa). A pausa em vigor vem do
  // operador (SDK ou motor): vale também depois de um F5, quando esta tela não escolheu a pausa.
  const activeBreakName = operatorState?.state === 'onBreak' ? operatorState.break?.name : undefined;
  const statusBadgeText = useMemo(() => {
    if (isError) return dialerNotice?.source === 'device' ? dialerNotice.title : 'Erro';
    if (isOnBreak) return currentBreakName || activeBreakName || 'Em pausa';
    return 'Disponível';
  }, [dialerNotice, isError, isOnBreak, currentBreakName, activeBreakName]);

  // Timer formatado da pausa (MM:SS ou H:MM:SS)
  const pauseDurationLabel = useMemo(() => {
    const hours = Math.floor(pauseDurationSeconds / 3600);
    const minutes = Math.floor((pauseDurationSeconds % 3600) / 60).toString().padStart(2, '0');
    const seconds = (pauseDurationSeconds % 60).toString().padStart(2, '0');
    if (hours > 0) {
      return `${hours}:${minutes}:${seconds}`;
    }
    return `${minutes}:${seconds}`;
  }, [pauseDurationSeconds]);

  const callDurationLabel = useMemo(() => {
    const m = Math.floor(callDurationSeconds / 60)
      .toString()
      .padStart(2, '0');
    const s = (callDurationSeconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  }, [callDurationSeconds]);

  // Render helpers
  const renderKey = (value: string, letters: string) => (
    <div 
      key={value} 
      className="key" 
      data-value={value} 
      onClick={() => handleDigit(value)}
      role="button"
      aria-label={`Tecla ${value}`}
    >
      <span className="number" aria-hidden="true">{value}</span>
      <span className="letters" aria-hidden="true">{letters || '\u00a0'}</span>
    </div>
  );

  // Se o painel de debug estiver aberto, renderizar apenas ele
  if (isDebugPanelOpen) {
    return (
      <DebugPanel onClose={() => {
        setIsDebugPanelOpen(false);
        addLog('info', 'Debug', '🔒 Painel de debug fechado');
      }} />
    );
  }

  return (
    <div className="phone-container" data-density={density === 'compact' ? 'compact' : 'comfortable'} style={{ '--notice-h': `${noticeHeight}px` } as CSSProperties}>
      {/* Dialer Screen */}
      <div className={`screen dialer-screen ${activeScreen === 'dialer' ? 'active' : ''}`} id="dialerScreen">
        <div className="dialer-header">
          <div className="status-container">
            <button className={`status-badge ${statusBadgeClass}`} id="statusBadge">
              <span className="status-dot" />
              {isOnBreak ? (
                <div className="status-info">
                  <span className="status-text">{statusBadgeText}</span>
                  <span className="pause-timer">{pauseDurationLabel}</span>
                </div>
              ) : (
                <span className="status-text">{statusBadgeText}</span>
              )}
            </button>
          </div>
          <div className="status-actions">
            {isOnBreak ? (
              <button className="resume-btn" id="resumeBtn" onClick={handleGoOffBreak}>
                <i className="fas fa-play" />
                <span>Voltar</span>
              </button>
            ) : (
              <button className="pause-btn" id="pauseBtn" onClick={() => setIsPauseModalOpen(true)}>
                <i className="fas fa-pause" />
                <span>Pausa</span>
              </button>
            )}
            {/* Sair vale para todas as abas abertas (e, no modo motor, encerra a sessão no servidor) */}
            <button
              type="button"
              className="logout-btn"
              id="logoutBtn"
              title="Sair do discador (todas as abas)"
              aria-label="Sair do discador"
              onClick={() => {
                if (typeof window !== 'undefined' && !window.confirm('Sair do discador em todas as abas?')) return;
                onLogout();
              }}
            >
              <i className="fas fa-right-from-bracket" />
            </button>
          </div>
        </div>

        <DialerNoticeBanner notice={dialerNotice} onDismiss={dismissDialerNotice} onRecheck={() => void recheckDevices()} onHeightChange={setNoticeHeight} />

        <div className="phone-number-display">
          <PhoneNumberInput
            id="phoneNumber"
            className="phone-input"
            placeholder=""
            aria-label="Número de telefone"
            value={dialValue}
            onChange={(digits) => setDialValue(digits.slice(0, 32))}
            onSubmit={() => {
              void handleCall();
            }}
          />
        </div>
        {dialPreview && <div className="dial-preview">Será discado: {dialPreview}</div>}

        <div className="keypad">
          {renderKey('1', '')}
          {renderKey('2', 'ABC')}
          {renderKey('3', 'DEF')}
          {renderKey('4', 'GHI')}
          {renderKey('5', 'JKL')}
          {renderKey('6', 'MNO')}
          {renderKey('7', 'PQRS')}
          {renderKey('8', 'TUV')}
          {renderKey('9', 'WXYZ')}
          {renderKey('*', '')}
          {renderKey('0', '+')}
          {renderKey('#', '')}
        </div>

        {callError && <div className="input-error">{callError}</div>}

        <div className="call-actions">
          <div className="empty-space" />
          <button className="call-button" id="callButton" onClick={handleCall} disabled={!formattedDial || !isIdle}>
            <i className="fas fa-phone" />
          </button>
          <button
            className="delete-button"
            id="deleteButton"
            style={{ visibility: dialValue ? 'visible' : 'hidden' }}
            onClick={handleBackspace}
          >
            <i className="fas fa-delete-left" />
          </button>
        </div>
      </div>

      {/* Call Screen */}
      <div className={`screen call-screen ${activeScreen === 'call' ? 'active' : ''}`} id="callScreen">
        <div className="call-info">
          <div className="call-status">
            <span className="operator-badge-small">{username?.slice(0, 2).toUpperCase() || 'OP'}</span>
            {isInCall ? (
              <span id="callTimer">{callDurationLabel}</span>
            ) : (
              <span id="callStatus">{callStatusLabel}</span>
            )}
          </div>
          <div className="call-number" id="callNumber">
            {formattedCallNumber}
          </div>
          <div className="call-location">Brasil</div>

          {/* Client Data Card - Campanha (dados buscados via API) */}
          {callInfo?.type === 'campaign' && (
            <div className="client-card" id="clientCard">
              {contactData.isLoading ? (
                <div className="client-card-loading">
                  <i className="fas fa-spinner fa-spin" />
                  <span>Carregando dados...</span>
                </div>
              ) : contactData.error ? (
                <div className="client-card-error">
                  <i className="fas fa-exclamation-circle" />
                  <span>{contactData.error}</span>
                </div>
              ) : (
                <>
                  {/* Header com avatar e info principal */}
                  <div className="client-card-header">
                    <i className="fas fa-user-circle client-avatar" />
                    <div className="client-main-info">
                      <span className="client-name" id="clientName">
                        {findValue(contactData.finalData, ['DECISOR', 'NOME', 'CLIENTE', 'PROSPECT']) ?? 
                         callInfo.info.campaignContact?.label ?? '--'}
                      </span>
                      <span className="client-company" id="clientCompany">
                        {findValue(contactData.finalData, ['EMPRESA', 'RAZAO SOCIAL', 'RAZÃO SOCIAL', 'RAZAO_SOCIAL']) ?? 
                         callInfo.info.campaign?.name ?? '--'}
                      </span>
                    </div>
                  </div>

                  <div className="client-card-divider" />

                  {/* Lista de informações do contato */}
                  <div className="client-info-list">
                    <div className="client-info-row">
                      <span className="client-info-label">Socio</span>
                      <span className="client-info-value">
                        {findValue(contactData.finalData, ['SOCIO', 'PROSPECT', 'DECISOR', 'NOME', 'CLIENTE']) ?? 
                         callInfo.info.campaignContact?.label ?? '--'}
                      </span>
                    </div>
                    <div className="client-info-row">
                      <span className="client-info-label">Empresa</span>
                      <span className="client-info-value">
                        {findValue(contactData.finalData, ['EMPRESA', 'RAZAO SOCIAL', 'RAZÃO SOCIAL', 'RAZAO_SOCIAL', 'COMPANY']) ?? '--'}
                      </span>
                    </div>
                    <div className="client-info-row">
                      <span className="client-info-label">CNPJ</span>
                      <span className="client-info-value">
                        {findValue(contactData.finalData, ['CNPJ', 'CPF_CNPJ', 'CPF/CNPJ', 'DOCUMENTO']) ?? '--'}
                      </span>
                    </div>
                    <div className="client-info-row">
                      <span className="client-info-label">Dívida federal ativa</span>
                      <span className="client-info-value debt">
                        {findValue(contactData.finalData, ['DIVIDA_ATIVA', 'DIVIDA ATIVA', 'DÍVIDA ATIVA', 'DIVIDA FEDERAL', 'DÍVIDA FEDERAL ATIVA', 'VALOR DIVIDA']) ?? 'R$ 0,00'}
                      </span>
                    </div>
                  </div>

                  <div className="client-card-divider" />

                  {/* Destaque para Capital a Recuperar */}
                  <div className="client-capital-section">
                    <span className="client-capital-label">Capital a recuperar</span>
                    <span className="client-capital-value">
                      {findValue(contactData.finalData, ['CAPITAL_RECUPERAR', 'CAPITAL A RECUPERAR', 'BENEFICIO', 'BENEFÍCIO', 'VALOR_TOTAL', 'VALOR RECUPERADO']) ?? 'R$ 0,00'}
                    </span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Legenda ao vivo e leitura da conversa (só no modo motor) */}
        <LiveCaptionPanel />

        <div className="call-controls">
          <div className="control-row">
            <button 
              className={`control-button ${isSpeakerOn ? 'active' : ''} ${speakerSupported === false ? 'disabled' : ''}`} 
              id="speakerBtn" 
              onClick={toggleSpeaker}
              disabled={speakerSupported === false}
              title={speakerSupported === false ? 'Viva-voz não suportado neste dispositivo' : ''}
            >
              <div className="control-icon">
                <i className={`fas ${isSpeakerOn ? 'fa-volume-high' : 'fa-volume-low'}`} />
              </div>
              <span>{isSpeakerOn ? 'Viva-voz' : 'Alto-falante'}</span>
            </button>
            <button className={`control-button ${inputMuted ? 'active' : ''}`} id="muteBtn" onClick={toggleMute}>
              <div className="control-icon">
                <i className={`fas ${inputMuted ? 'fa-microphone-slash' : 'fa-microphone'}`} />
              </div>
              <span>{inputMuted ? 'Mudo' : 'Mudo'}</span>
            </button>
            <button className="control-button" id="keypadBtn" onClick={() => setActiveScreen('incallKeypad')}>
              <div className="control-icon">
                <i className="fas fa-th" />
              </div>
              <span>Teclado</span>
            </button>
          </div>
          <div className="control-row control-row-center">
            <button className="control-button whatsapp-btn" id="whatsappBtn" onClick={handleWhatsApp}>
              <div className="control-icon whatsapp-icon">
                <i className="fab fa-whatsapp" />
              </div>
              <span>WhatsApp</span>
            </button>
            <div className="end-call-wrapper">
              <button className="end-call-button" id="endCallButton" onClick={handleHangup}>
                <i className="fas fa-phone-slash" />
              </button>
              <span className="end-call-label">Desligar</span>
            </div>
          </div>
        </div>
      </div>

      {/* In-Call Keypad */}
      <div className={`screen incall-keypad-screen ${activeScreen === 'incallKeypad' ? 'active' : ''}`} id="incallKeypadScreen">
        <div className="incall-header">
          <button className="hide-keypad-btn" id="hideKeypadBtn" onClick={() => setActiveScreen('call')}>
            <span>Ocultar</span>
          </button>
        </div>
        <div className="incall-number-display">
          <span id="incallPhoneNumber">{formattedCallNumber}</span>
        </div>
        <div className="keypad incall-keypad">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map((digit) => (
            <div key={digit} className="key incall-key" data-value={digit} onClick={() => handleDigit(digit)}>
              <span className="number">{digit}</span>
              <span className="letters">&nbsp;</span>
            </div>
          ))}
        </div>
        <div className="incall-actions">
          <button className="end-call-button" id="endCallButton2" onClick={handleHangup}>
            <i className="fas fa-phone-slash" />
          </button>
        </div>
      </div>

      {/* Call History Screen */}
      <div className={`screen call-history-screen ${activeScreen === 'history' ? 'active' : ''}`} id="callHistoryScreen">
        <CallHistoryList
          items={historySearchResults ?? callHistory}
          isLoading={isLoadingHistory}
          error={historyError}
          onRefresh={fetchCallHistory}
          query={historyQuery}
          onQueryChange={handleHistoryQueryChange}
          isSearching={isSearchingHistory}
          searchHint={historySearchHint}
          onCall={(call) => {
            haptic.impactLight();
            setDialValue(historyPhoneDigits(call));
            setActiveScreen('dialer');
          }}
          onWhatsApp={(call) => {
            haptic.impactLight();
            const whatsappNumber = `55${historyPhoneDigits(call)}`;
            const operatorName = userData?.name || username || '';
            const message = generateWhatsAppMessage(operatorName, call.fields);
            window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`, '_blank');
          }}
          apiBaseUrl={API_BASE_URL}
          getAuthHeaders={getAuthHeaders}
        />
      </div>

      {/* Bottom Navigation - ESCONDIDO durante chamada */}
      <nav className={`bottom-nav ${activeScreen === 'call' || activeScreen === 'incallKeypad' ? 'hidden' : ''}`} id="bottomNav">
        <div
          className={`nav-item ${activeScreen === 'history' ? 'active' : ''}`}
          id="navCalls"
          onClick={() => setActiveScreen('history')}
        >
          <div className="nav-icon">
            <i className="fas fa-clock-rotate-left" />
          </div>
          <span>Recentes</span>
        </div>
        <div
          className={`nav-item ${activeScreen === 'dialer' || activeScreen === 'call' || activeScreen === 'incallKeypad' ? 'active' : ''}`}
          id="navKeypad"
          onClick={() => setActiveScreen('dialer')}
        >
          <div className="nav-icon">
            <i className="fas fa-th" />
          </div>
          <span>Teclado</span>
        </div>
      </nav>

      {/* Pause Modal */}
      <div className={`modal-overlay ${isPauseModalOpen ? 'active' : ''}`} id="pauseModal">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="modal-title">Selecionar Pausa</h2>
            <button className="modal-close" id="closePauseModal" onClick={() => setIsPauseModalOpen(false)}>
              <i className="fas fa-xmark" />
            </button>
          </div>
          <div className="pause-options" id="pauseOptions">
            {breaks.map((item: { id: number; name: string; icon?: string }) => (
              <div key={item.id} className="pause-option" onClick={() => handleGoOnBreak(item.id)}>
                <div className="pause-option-icon">
                  <i className={`fas ${item.icon || 'fa-circle-pause'}`} />
                </div>
                <div className="pause-option-info">
                  <div className="pause-option-name">{item.name}</div>
                  <div className="pause-option-id">ID: {item.id}</div>
                </div>
              </div>
            ))}
            {!breaks.length && <div className="history-empty">Nenhuma pausa configurada.</div>}
          </div>
          <button className="modal-cancel-btn" id="cancelPauseBtn" onClick={() => setIsPauseModalOpen(false)}>
            Cancelar
          </button>
        </div>
      </div>

      {/* Qualification Modal */}
      <div className={`modal-overlay qualification-modal ${isQualificationOpen ? 'active' : ''}`} id="qualificationModal">
        <div className="qualification-sheet">
          <div className="sheet-handle" />

          <div className="sheet-header">
            <span className="sheet-title">Qualificar Chamada</span>
            <span className="sheet-subtitle">Registre o resultado do atendimento</span>
          </div>

          <div className="result-segmented">
            <button
              className={`segment ${qualificationResult === 'success' ? 'active' : ''}`}
              id="resultSuccess"
              data-type="success"
              onClick={() => {
                setQualificationResult('success');
                setSelectedQualificationId(null);
              }}
            >
              Sucesso
            </button>
            <button
              className={`segment ${qualificationResult === 'failure' ? 'active' : ''}`}
              id="resultFailure"
              data-type="failure"
              onClick={() => {
                setQualificationResult('failure');
                setSelectedQualificationId(null);
              }}
            >
              Falha
            </button>
            <div className="segment-indicator" />
          </div>

          <div className="qualifications-container">
            <div className="qualification-list" id="qualificationList">
              {(qualificationResult === 'success' ? successQualifications : discardQualifications).map((q) => (
                <button
                  key={q.id}
                  className={`qualification-item ${selectedQualificationId === q.id ? 'selected' : ''}`}
                  onClick={() => setSelectedQualificationId(q.id)}
                >
                  <span>{q.name}</span>
                </button>
              ))}
              {!(qualificationResult === 'success' ? successQualifications : discardQualifications).length && (
                <div className="history-empty">Nenhuma qualificação configurada.</div>
              )}
            </div>
          </div>

          {qualificationError && <div className="input-error">{qualificationError}</div>}

          <div className="sheet-footer">
            <button className="confirm-btn" id="confirmQualificationBtn" onClick={handleQualificationConfirm} disabled={!selectedQualificationId}>
              Confirmar
            </button>
          </div>
        </div>
      </div>

      {/* Modal de informações do cliente (apenas quando showClientInfoModal=true) */}
      {showClientInfoModal && (
        <DraggableClientInfoModal
          isOpen={isClientInfoModalOpen}
          campaignCallInfo={campaignCallInfo}
          campaignContactPhoneId={currentCall?.data?.campaignContactPhoneId ?? null}
          callDuration={callDurationSeconds}
          isCallActive={isInCall || isConnecting}
          onClose={() => setIsClientInfoModalOpen(false)}
        />
      )}
    </div>
  );
}
